import { useAccounts } from '@api/hooks';
import type { Account } from '@api/hooks/useAccounts';
import { FiltersModal } from '@components/FiltersModal/FiltersModal';
import { Checkbox, SelectInput, Typography } from '@gurupay/gp-ui-library';
import type { StatementFiltersProps } from '@hooks/useAllRequestedStatements';
import { useClearFilter } from '@hooks/useClearFilter.ts';
import { useDebounce } from '@hooks/useDebounce';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AVAILABLE_FILE_FORMATS } from './GenerateStatementsView';

type StatementsFilters = {
	onApply: (values: StatementFiltersProps) => void;
};

const StatementsFilters = ({ onApply }: StatementsFilters) => {
	const { t } = useTranslation('', { keyPrefix: 'statements' });
	const [ibanSearchParam, setIbanSearchParam] = useState('');
	const debouncedIban = useDebounce(ibanSearchParam.trim());
	const filterForm = useFormContext<StatementFiltersProps>();
	const { handleSubmit, control } = filterForm;

	const { data: accounts, isLoading: isIbansLoading } = useAccounts({
		limit: 100,
		iban: debouncedIban,
	});

	const ibanOptions =
		accounts?.items?.map((acc: Account) => ({
			label: acc.iban,
			value: acc.iban,
		})) ?? [];

	const onSubmit = (data: StatementFiltersProps) => {
		const trimmedIban = data.iban?.trim();

		onApply({
			iban: trimmedIban,
			type: data.type,
		});
	};

	const { hasFilters, handleClear } = useClearFilter(filterForm, onSubmit);

	return (
		<FiltersModal
			hasFilters={hasFilters}
			onClear={handleClear}
			onSubmit={handleSubmit(onSubmit)}
		>
			<Controller
				control={control}
				name="iban"
				render={({ field }) => (
					<SelectInput
						{...field}
						className="max-w-[500px]"
						isLoading={isIbansLoading}
						label={t('accounts')}
						onSearch={setIbanSearchParam}
						options={ibanOptions}
						showClear
					/>
				)}
			/>

			<Typography level="h3">{t('fileFormat')}</Typography>

			<Controller
				control={control}
				name="type"
				render={({ field }) => {
					const selected = field.value ?? [];

					return (
						<div className="flex gap-space-16 flex-wrap">
							{AVAILABLE_FILE_FORMATS.map((format) => {
								const checked = selected.includes(format.value);

								return (
									<Checkbox
										key={format.value}
										checked={checked}
										label={format.label}
										onChange={(nextChecked: boolean) => {
											field.onChange(
												nextChecked
													? [...selected, format.value]
													: selected.filter(
															(selectedItem) => selectedItem !== format.value,
														),
											);
										}}
									/>
								);
							})}
						</div>
					);
				}}
			/>
		</FiltersModal>
	);
};

export default StatementsFilters;
